import { formatUserName } from "@impero-com/common/utils/formatting";
import { TFunction } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";

import { ExistingControlResult } from "api/generated/model";
import { getFinalStepForExistingControlResult } from "utils/controlResults/activityStatusUtils";
import { ActorName, localizeActorName } from "utils/controlResults/actorName";
import { LookupContext } from "utils/controlResults/lookupContext";

import {
    CONTROL_PROGRAM_STATES,
    ControlProgramState,
} from "../controlProgramStatusFilter";
import { ControlActivityTableRow } from "../types";

export function computeDueDate(controlResult: ExistingControlResult): string {
    const finalStep = getFinalStepForExistingControlResult(controlResult);
    if (!finalStep) {
        // No pending step means the activity is completed, so we can return the due date of the initial step.
        return controlResult.stepGroup.initialStep.dueDate;
    }
    return finalStep.dueDate;
}

export function computeDefaultClaimant(
    controlResult: ExistingControlResult,
    lookupContext: LookupContext
): string | null {
    const initialStep = controlResult.stepGroup.initialStep;
    if (initialStep.kind.type === "Pool") {
        const poolId = initialStep.kind.userPoolId;
        const pool = lookupContext.controlResultUserPoolsById.get(poolId);
        if (pool && pool.claimantUserId) {
            const defaultClaimant = lookupContext.usersById.get(
                pool.claimantUserId
            );
            return defaultClaimant ? formatUserName(defaultClaimant) : null;
        }
    }
    return null;
}

export function localizePerformerName(
    name: ActorName | null,
    t: TFunction
): string | null {
    return name && localizeActorName(name, t);
}

export function getResponsibleOrReviewerNames(
    row: Pick<
        ControlActivityTableRow,
        | "responsible"
        | "performer"
        | "reviewer"
        | "reviewPerformer"
        | "secondReviewer"
        | "secondReviewPerformer"
    >,
    t: TFunction
): string[] {
    return [
        row.responsible,
        localizePerformerName(row.performer, t),
        row.reviewer,
        localizePerformerName(row.reviewPerformer, t),
        row.secondReviewer,
        localizePerformerName(row.secondReviewPerformer, t),
    ].filter((name): name is string => name !== null);
}

// Matches when the row's values include at least one of the selected values.
// An empty/absent filter matches everything.
export function includesSomeFilterFn(
    rowValues: string[],
    value: string[] | null
): boolean {
    if (!value || value.length === 0) {
        return true;
    }
    return value.some((selected) => rowValues.includes(selected));
}

export function localizeControlProgramStatus(
    state: ControlProgramState,
    t: TFunction
): string {
    switch (state) {
        case CONTROL_PROGRAM_STATES.ACTIVE:
            return t("ControlActivitiesList_ControlProgramStatus_Active");
        case CONTROL_PROGRAM_STATES.INACTIVE:
            return t("ControlActivitiesList_ControlProgramStatus_Inactive");
        case CONTROL_PROGRAM_STATES.DELETED:
            return t("ControlActivitiesList_ControlProgramStatus_Deleted");
        default:
            return assertNever(state);
    }
}

export function localizeArchived(archived: boolean, t: TFunction): string {
    if (archived) {
        return t("ControlActivitiesList_Archived_Cell");
    } else {
        return t("ControlActivitiesList_NotArchived_Cell");
    }
}
